import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SITE, waLink } from "@/lib/site";

const SERVICES = [
  "General ENT Consultation",
  "Ear / Hearing / Tinnitus",
  "Vertigo & Balance",
  "Sinus & Nasal Blockage",
  "Throat / Tonsils / Voice",
  "Children's ENT",
  "Advanced ENT Surgery",
];

export default function AppointmentForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    preferred_date: "",
    message: "",
  });

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const buildWaMessage = () =>
    `New appointment request%0A---------------------%0A` +
    `Name: ${form.name}%0A` +
    `Phone: ${form.phone}%0A` +
    (form.email ? `Email: ${form.email}%0A` : "") +
    (form.service ? `Concern: ${form.service}%0A` : "") +
    (form.preferred_date ? `Preferred date: ${form.preferred_date}%0A` : "") +
    (form.message ? `Message: ${form.message}` : "");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    toast.success("Opening WhatsApp with your appointment details...");
    const url = `https://wa.me/${SITE.whatsapp}?text=${buildWaMessage()}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const inputCls =
    "bg-transparent border-0 border-b border-brand-line rounded-none px-0 h-11 text-brand-ink placeholder:text-brand-muted/60 focus-visible:ring-0 focus-visible:border-brand-pine transition-colors";

  return (
    <form onSubmit={handleSubmit} data-testid="appointment-form" className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="ap-name" className="text-xs tracking-[0.15em] uppercase text-brand-muted">
            Full name *
          </Label>
          <Input
            id="ap-name"
            data-testid="appointment-name-input"
            value={form.name}
            onChange={update("name")}
            placeholder="Your name"
            className={inputCls}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ap-phone" className="text-xs tracking-[0.15em] uppercase text-brand-muted">
            Phone *
          </Label>
          <Input
            id="ap-phone"
            data-testid="appointment-phone-input"
            value={form.phone}
            onChange={update("phone")}
            placeholder="+91 ..."
            className={inputCls}
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="ap-email" className="text-xs tracking-[0.15em] uppercase text-brand-muted">
            Email
          </Label>
          <Input
            id="ap-email"
            type="email"
            data-testid="appointment-email-input"
            value={form.email}
            onChange={update("email")}
            placeholder="you@email.com"
            className={inputCls}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ap-date" className="text-xs tracking-[0.15em] uppercase text-brand-muted">
            Preferred date
          </Label>
          <Input
            id="ap-date"
            type="date"
            data-testid="appointment-date-input"
            value={form.preferred_date}
            onChange={update("preferred_date")}
            className={inputCls}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs tracking-[0.15em] uppercase text-brand-muted">Your concern</Label>
        <Select value={form.service} onValueChange={update("service")}>
          <SelectTrigger
            data-testid="appointment-service-select"
            className="bg-transparent border-0 border-b border-brand-line rounded-none px-0 h-11 text-brand-ink focus:ring-0 data-[placeholder]:text-brand-muted/60"
          >
            <SelectValue placeholder="Select a concern" />
          </SelectTrigger>
          <SelectContent>
            {SERVICES.map((s) => (
              <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="ap-msg" className="text-xs tracking-[0.15em] uppercase text-brand-muted">
          Message
        </Label>
        <Textarea
          id="ap-msg"
          data-testid="appointment-message-input"
          value={form.message}
          onChange={update("message")}
          placeholder="Briefly describe your symptoms (optional)"
          rows={3}
          className="bg-transparent border border-brand-line rounded-md text-brand-ink placeholder:text-brand-muted/60 focus-visible:ring-0 focus-visible:border-brand-pine transition-colors"
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <button
          type="submit"
          data-testid="appointment-submit-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-teal px-7 py-3.5 text-sm text-brand-ink transition-[background-color,transform] duration-200 hover:bg-brand-tealHover hover:scale-[1.02]"
        >
          <Send className="w-4 h-4" />
          Send via WhatsApp
        </button>
        <a
          href={waLink(`Hello Dr. Teza's clinic, I would like to book an appointment.`)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="appointment-direct-whatsapp-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-whatsapp px-7 py-3.5 text-sm text-whatsapp transition-colors hover:bg-whatsapp hover:text-white"
        >
          Chat directly
        </a>
      </div>
    </form>
  );
}
